import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Col } from 'antd';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { isThirdPartySupplier, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS } from '../../actions/refrigerant_events_actions';
import {
	refreshRefrigerantEventTypeCounts,
	refrigerantEventsRestCrudThunks,
} from '../../thunks/refrigerant_events_thunks';
import { searchAssetsLiteForRole } from '../../thunks/assets_thunks';
import { useFilters } from '../../hooks/useFilters';
import { getRefrigerantEventFilterConfig } from '../../utils/AssetFilterUtils';
import { withUserType } from '../withUserType';
import {
	REFRIGERANT_EVENTS_TC,
	REFRIGERANT_EVENT_SORTERS,
	REFRIGERANT_EVENT_TYPES_FILTER_KEY,
	REFRIGERANT_EVENT_TYPE_PIPELINE_FILTERS,
} from '../../utils/RefrigerantUtils';
import getRefrigerantEventLedgerColumns from '../refrigerant/RefrigerantEventLedgerColumns';
import RefrigerantSubnav, { REFRIGERANT_SUBNAV_KEYS } from '../refrigerant/RefrigerantSubnav';
import LogRefrigerantEventDrawer from '../refrigerant/LogRefrigerantEventDrawer';

/**
 * The filter-OPTION dropdowns (assets, locations, asset types) reuse the shared
 * `assetsAdvancedFilters` collection rather than the refrigerant-specific
 * `REFRIGERANT_EVENTS_ADVANCED_FILTERS`. Each async filter looks its target collection up in its OWN
 * entity slice, so a bespoke name would have to be registered in every reducer the filters read from
 * (assets, locations, regions, asset_types, …) or the dropdown throws "invalid target collection name"
 * on render. It only ever holds fetched dropdown options — the page's own list, filters and paging live
 * in REFRIGERANT_EVENTS_TC — so sharing it with the assets index is safe. Same constant, and the same
 * reasoning, as RefrigerantAssetsIndexPage.
 *
 * Note this page is the first to offer the ASSET filter alongside the other two, and the asset options
 * are the only ones that come out of `state.assets` — which was the one reducer of the seven that had
 * never registered this collection. It is registered now; do not assume the others cover it.
 */
const REFRIGERANT_EVENTS_FILTER_OPTIONS_TC = 'assetsAdvancedFilters';

/**
 * Refrigerant Tracking ▸ Event Log — the whole tenant's servicing record, newest first.
 *
 * The ledger is append-only, so there is no row click-through to an edit form and no bulk action: a
 * correction is a NEW event, logged through the same drawer, and the record keeps both entries.
 */
const RefrigerantEventLogPage: FC<any> = ({
	refrigerantEvents,
	currentUser,
	userType,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	refreshCounts,
	searchAssetsLite,
}) => {
	const [logEventVisible, setLogEventVisible] = useState(false);

	// See the comment on the button below.
	const canLogEvent = !isThirdPartySupplier(currentUser);
	const [refreshToken, setRefreshToken] = useState(0);

	const columns = useMemo(
		() => getRefrigerantEventLedgerColumns({ userType, showAsset: true }),
		[userType]
	);

	const fetchEvents = useMemo(() => refrigerantEventsRestCrudThunks(userType).read, [userType]);

	const allFilters = useFilters({
		filterTargetCollectionName: REFRIGERANT_EVENTS_FILTER_OPTIONS_TC,
		overviewCollectionName: REFRIGERANT_EVENTS_TC,
		updateFilters: useCallback(
			(filters) => updateFilters(filters, REFRIGERANT_EVENTS_TC),
			[updateFilters]
		),
	});

	const filterConfig = useMemo(
		() => getRefrigerantEventFilterConfig({ filters: allFilters, currentUser, searchAssetsLite }),
		[allFilters, currentUser, searchAssetsLite]
	);

	const onLogEventSuccess = useCallback(() => {
		setLogEventVisible(false);
		setRefreshToken((token) => token + 1);
	}, []);

	return (
		<>
			<PaginatedReduxTableWithHeader
				key={refreshToken}
				targetCollectionName={REFRIGERANT_EVENTS_TC}
				entityCollectionName="refrigerant_events"
				stateSlice={refrigerantEvents}
				updateFilters={updateFilters}
				updateSorters={updateSorters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				refreshCounts={refreshCounts}
				pipeLineFilters={REFRIGERANT_EVENT_TYPE_PIPELINE_FILTERS}
				overridePipelineFilterKey={REFRIGERANT_EVENT_TYPES_FILTER_KEY}
				filterConfig={filterConfig}
				sorters={REFRIGERANT_EVENT_SORTERS}
				initialSorters={{ sort_by: 'occurredAt', order: 'descend' }}
				initialPagination={{ current: 1, pageSize: 25 }}
				tableColumns={columns}
				showHeader
				// The header's search box writes the `search` key, which the ledger now matches against
				// the note text, the appliance's name and number, and the work order id.
				fetchData={fetchEvents}
				leftActions={
					<RefrigerantSubnav activeKey={REFRIGERANT_SUBNAV_KEYS.EVENT_LOG} userType={userType} />
				}
				// The subnav switches between two different ledgers; the event-type chips filter within
				// whichever one is showing. The tabs therefore have to sit above the chips.
				pipelineFiltersBelowHeader
				rightActions={
					<Col style={{ textAlign: 'right' }}>
						{/* A third-party supplier is excluded on top of MODIFY_ASSETS. Neither
						    `AccessPermissionChecker` nor `OWButtonWithSupplierManagedLock` covers this
						    persona — the former checks the permission, the latter locks supplier-MANAGED
						    buyers — and the server refuses their event POST outright without a private
						    buyer company (see refrigerant_events_thunks). Without the gate the drawer opens,
						    the tech fills in a regulated reading, and the save cannot succeed. Same rule as
						    `canLogEvent` on the asset detail tab. */}
						{canLogEvent ? (
							<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
								<OWButtonWithSupplierManagedLock
									size="large"
									type="primary"
									onClick={() => setLogEventVisible(true)}
								>
									Log Event
								</OWButtonWithSupplierManagedLock>
							</AccessPermissionChecker>
						) : null}
					</Col>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No refrigerant events"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No refrigerant events logged yet.'}
						body={
							<div style={{ textAlign: 'center', maxWidth: 440 }}>
								Charges, recoveries, repairs, inspections and verification tests all land here — it
								is the servicing record an auditor reads.
							</div>
						}
					/>
				}
			/>
			{logEventVisible ? (
				<LogRefrigerantEventDrawer
					visible
					onCancel={() => setLogEventVisible(false)}
					onSuccess={onLogEventSuccess}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	refrigerantEvents: state.refrigerant_events,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	refreshCounts: (filters) =>
		dispatch(refreshRefrigerantEventTypeCounts(ownProps.userType)(filters)),
	// The Asset filter's type-to-search goes through the OpenSearch lite search rather than the shared
	// SQL `readLite`, so it matches the same index the asset screens read. Bound here and passed into
	// the filter config so the other pages offering an Asset filter are untouched.
	//
	// Scoped to REFRIGERANT-TRACKED appliances. Only a tracked appliance can carry a 608 event, so every
	// other asset in the tenant is an option that returns an empty ledger — and on an estate where a
	// handful of appliances are enrolled out of thousands of assets, an unscoped picker buries the ones
	// that can match. Same scope the log-event drawer's own asset picker applies.
	searchAssetsLite: (params, targetCollectionName, pagination, sorting, filters, add) =>
		dispatch(
			searchAssetsLiteForRole(ownProps.userType)(
				{ ...(params || {}), isRefrigerantTracked: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				add
			)
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(RefrigerantEventLogPage as any)
	) as any
);
